/**
 * Wahlkampf im Zwei-Spieler-Modus: Beide Spieler führen ihren eigenen Wahlkampf gleichzeitig
 * (Kernthemen, Tour, TV-Duell mit denselben Fragen). Ist der Wahlkampf beider fertig, wird
 * gewählt und die Regierungsbildung beginnt (slots.ts).
 */
import { STOPS } from '../content/economy';
import { WENDE_COST, snapPub } from './zeitgeist';
import { funding, adEffect, receptivity, setAlloc } from './ads';
import { buildDuel, duelEvalExtra } from './duel';
import { AXN } from '../content/axes';
import { ISSUES } from '../content/issues';
import { fatigue } from '../content/settings';
import { ISS } from '../content/states';
import { debtLimit } from './budget';
import { lawOn, tourEff } from './campaign';
import { electionResult } from './election';
import { poll } from './govern';
import { pub } from './opinion';
import { rnd } from './rng';
import { PN } from './rules';
import { S, log } from './state';
import { clamp, dateYear, de } from './util';
import { nextLegislature, unslot } from './slots';
import { ROLES, pl, me, isChan, isVersus, fitOf, type Player, type PCamp } from './versus';
import { issuesNow } from './axes';

/** Programm einer Spielerpartei (auf dem Kanzler-Platz steht es in S.you). */
const posOfP = (P: Player): Record<string, number> => (isChan(P.role) ? S.you : P.party!.pos);
/** Regiert die Partei des Spielers gerade mit (Kanzler oder Partner)? */
const inGov = (P: Player) => !S.mp!.first && (isChan(P.role) || P.role2 === 'partner');

function optsFor(P: Player): PCamp['opts'] {
  const first = S.mp!.first;
  const list = (first ? issuesNow().filter((i: any) => i.at === 0).map((i: any) => ({ id: i.id, n: i.n, sev: i.sev }))
    : issuesNow().filter((i: any) => S.issues[i.id]?.on).map((i: any) => ({ id: i.id, n: i.n, sev: S.issues[i.id].sev }))).sort((a: any, b: any) => b.sev - a.sev);
  const pos = posOfP(P);
  const ax = Object.keys(pos).filter((a) => Math.abs(pos[a]) >= 1).sort((a, b) => Math.abs(pos[b]) - Math.abs(pos[a])).slice(0, 4).map((a) => ({ id: 'ax:' + a, n: AXN[a], ax: a }));
  return [...list.slice(0, 5), { id: 'wirt', n: 'Wirtschaft und Arbeitsplätze' }, ...ax];
}

/** Wert eines Kernthemas für einen Spieler: Regierende punkten mit Bilanz, Opposition mit Angriffen. */
export function themeValFor(P: Player, id: string): number {
  const first = S.mp!.first, gov = inGov(P), c = P.camp!;
  if (id.startsWith('ax:')) { const a = id.slice(3), y = posOfP(P)[a] ?? 0, fit = 1 - Math.abs(y - pub(a)) / 2; return clamp((fit - .35) * 2.24 * Math.abs(y) / 2, -1.5, 1.3); }
  if (id === 'wirt') {
    if (first) return .5;
    const v = (S.gr - .6) * 1.2 - (S.al - 6.2) * .8 + (S.saldo > debtLimit() ? .3 : -.3);
    return clamp(gov ? v : -v + .3, -1.5, 1.5);
  }
  const I = ISS[id], o = c.opts.find((x) => x.id === id); if (!I || !o) return 0;
  const sev = o.sev ?? 50, sal = Object.values<number>(I.imp).reduce((a, b) => a + b, 0);
  const v = first || !gov ? (sev - 45) / 20 + .3 : (lawOn(id, S.leg) ? .9 : -.4) + (55 - sev) / 25;
  return clamp(v * (.6 + sal * .25), -1.8, 1.8);
}

/** Gemeinsame Fragen des TV-Duells. */
function duelQs() {
  return buildDuel({ first: S.mp!.first, gov: false, opp: 'Die Konkurrenz', coalition: false });
}
function duelEffectFor(P: Player, o: any): [number, string] {
  const R = rnd(), gov = inGov(P);
  switch (o.k) {
    case 'issue':
    case 'plan': return gov ? (lawOn(o.iss, S.leg) ? [1.2, 'Die Bilanz überzeugt. Ihr könnt Ergebnisse vorweisen.'] : [-.6, 'Das wirkt hohl. Eure Regierung hat dazu nichts beschlossen.']) : [.9, 'Der Plan klingt durchdacht und trifft einen Nerv.'];
    case 'promise': return [1, 'Das Versprechen kommt gut an.'];
    case 'risky': return R < .5 ? [1.6, 'Der Angriff sitzt. Die Konkurrenz wirkt verunsichert.'] : [-1, 'Der Angriff geht nach hinten los und wirkt unsouverän.'];
    case 'tax': return [1, 'Das hören die Zuschauer gern.'];
    case 'honest': return S.saldo < debtLimit() + 3 ? [.6, 'Ehrlichkeit wird anerkannt, die Lage ist ernst.'] : [-.3, 'Unnötig düster, die Haushaltslage gibt das nicht her.'];
    case 'invest': return (posOfP(P).haushalt ?? 0) >= 1 ? [.8, 'Glaubwürdig, das passt zu eurem Programm.'] : [-.2, 'Das nimmt euch niemand ab, euer Programm sagt anderes.'];
    case 'open': return [.3, 'Solide, aber wenig mitreißend.'];
    case 'clear': return R < .6 ? [.9, 'Die klare Ansage verschafft euch Profil.'] : [-.4, 'Die Festlegung verprellt Wechselwähler.'];
    case 'exclude': return [.6, 'Die klare Abgrenzung wird von vielen begrüßt.'];
    case 'admit': return [.6, 'Die Selbstkritik wirkt glaubwürdig.'];
    case 'reject': return R < .5 ? [.8, 'Souverän pariert.'] : [-.7, 'Das wirkt dünnhäutig.'];
    case 'humor': return R < .6 ? [1.2, 'Das Studio lacht. Ein Punkt für euch.'] : [-.6, 'Der Witz zündet nicht.'];
  }
  return duelEvalExtra(o, posOfP(P), gov, R) ?? [0, ''];
}

const players = (): Player[] => ROLES.map(pl).filter((p) => !!p.party);
export const duelSum = (P: Player) => (P.camp?.ans ?? []).reduce((s, a) => s + a.v, 0);

/** Wahlkampf beginnt für beide Spieler. */
export function mpCampStart() {
  if (!isVersus()) return;
  const M = S.mp!;
  if (M.first ? S.phase !== 'program' : !['govern', 'review', 'end'].includes(S.phase)) return;
  if (players().length < 2) return;
  if (!S.mood || !Object.keys(S.mood).length) S.mood = { rent: 46, fam: 45, arb: 44, unt: 47, jung: 45 };
  for (const P of players()) P.camp = { step: 'themen', opts: [], themes: [], stops: {}, ans: [], done: false };
  for (const P of players()) {
    P.camp!.opts = optsFor(P);
    const share = M.first ? P.party!.base : isChan(P.role) ? poll() : clamp(P.party!.base + P.bonus, 2, 60);
    const last = M.first ? null : (S.res?.share?.[isChan(P.role) ? 'YOU' : P.pid] ?? null);
    P.camp!.fund = funding({ share, pos: posOfP(P), first: M.first, arbg: S.lob?.arbg, soz: S.lob?.soz, gew: S.lob?.gew, lastShare: last });
    P.camp!.ads = {};
  }
  M.duelQ = duelQs();
  S.phase = 'campaign'; S.camp = null; S.editing = null; S.bview = false;
  log(M.first ? 'Der Wahlkampf zur ersten Bundestagswahl beginnt.' : `Der Wahlkampf beginnt${S.early ? ' (vorgezogene Neuwahl)' : ''}.`);
}

const myCamp = (): [Player, PCamp] | null => { const P = me(); return P && P.camp && !P.camp.done && S.phase === 'campaign' ? [P, P.camp] : null; };

export function mpTheme(id: string) {
  const x = myCamp(); if (!x) return; const [, c] = x;
  if (!c.opts.some((o) => o.id === id)) return;
  c.themes = c.themes.includes(id) ? c.themes.filter((t) => t !== id) : c.themes.length < 3 ? [...c.themes, id] : c.themes;
}
export function mpStep(step: PCamp['step']) {
  const x = myCamp(); if (!x) return; const [, c] = x;
  if (!['themen', 'tour', 'werbung', 'duell', 'fazit'].includes(step)) return;
  if (step !== 'themen' && c.themes.length !== 3) return;
  if (step === 'fazit' && c.ans.length < (S.mp!.duelQ?.length ?? 0)) return;
  c.step = step;
}
export const stopsOf = (c: PCamp) => Object.values(c.stops).reduce((a, b) => a + b, 0);
export function mpStop(id: string) {
  const x = myCamp(); if (!x) return; const [, c] = x;
  if (stopsOf(c) < STOPS && (c.stops[id] || 0) < 3) c.stops[id] = (c.stops[id] || 0) + 1;
}
export function mpStopsReset() { const x = myCamp(); if (x) x[1].stops = {}; }
export function mpAnswer(i: number) {
  const x = myCamp(); if (!x) return; const [P, c] = x;
  const q = S.mp!.duelQ?.[c.ans.length]; if (!q || !q.o[i]) return;
  const [v, txt] = duelEffectFor(P, q.o[i]);
  c.ans.push({ i, v, txt });
}

/** Stimmen-Effekt des Wahlkampfs in Prozentpunkten. */
export function campTot(P: Player) {
  const c = P.camp!, others = players().filter((o) => o !== P);
  const th = c.themes.reduce((s, id) => s + themeValFor(P, id), 0) * .55;
  const tour = tourEff(c.stops), duel = clamp(duelSum(P), -3, 3) * .6;
  const vs = others.length ? clamp((duelSum(P) - duelSum(others[0])) * .15, -.6, .6) : 0;
  const prog = clamp((fitOf(posOfP(P)) - .75) * 10, -2, 2);
  const wende = -WENDE_COST * (P.wende || 0);
  const ads = adEffect(c.ads || {}, receptivity(c.themes, S.mood, inGov(P)), S.mp!.first).tot;
  return { th, tour, duel, vs, prog, wende, ads, base: -1.2, tot: th + tour + duel + vs + prog + wende + ads - 1.2 };
}

/** Spieler beendet seinen Wahlkampf. Sind beide fertig, wird gewählt. */
export function mpCampDone() {
  const x = myCamp(); if (!x) return; const [P, c] = x;
  if (c.themes.length !== 3 || c.ans.length < (S.mp!.duelQ?.length ?? 0)) return;
  c.done = true; c.tot = campTot(P).tot;
  log(`${isChan(P.role) ? PN('YOU') : PN(P.pid)} beendet den Wahlkampf.`);
  if (players().every((p) => p.camp?.done)) mpElection();
}

/** Bundestagswahl im Mehrspieler: Spielerparteien nach Wahlkampf, Kanzler-Platz nach Umfrage. */
export function mpElection() {
  const M = S.mp!, first = M.first, fixed: Record<string, number> = {};
  let you = 0;
  const swing = () => rnd() * 3 - 1.5;
  for (const P of players()) {
    const tot = P.camp?.tot ?? 0;
    if (isChan(P.role)) you = clamp(poll() - S.leg * fatigue() - (S.early ? 2 : 0) + swing() + tot, 4, 60);
    else fixed[P.pid] = clamp(P.party!.base + (first ? 0 : P.bonus) + tot + rnd() * 2 - 1, 2, 60);
  }
  if (M.chan === 'ai') you = clamp(poll() - S.leg * fatigue() - (S.early ? 2 : 0) + swing(), 4, 60);
  const r = electionResult(you, fixed);
  S.res = r; S.final = you;
  S.elYear = first ? 2029 : dateYear(S.m);
  const show = players().map((P) => `${isChan(P.role) ? S.name : P.party!.n} ${de(isChan(P.role) ? r.share.YOU : r.share[P.pid])} %`).join(', ');
  log(`Bundestagswahl ${S.elYear}: ${show}.`, '');
  for (const P of players()) { P.camp = null; P.wende = 0; }
  snapPub(pub);
  M.duelQ = null;
  unslot();
  nextLegislature(first);
}

export const isPlayerCampaign = () => isVersus() && S.phase === 'campaign' && players().some((p) => !!p.camp);

/** Spieler verteilt sein Wahlkampfbudget auf einen Kanal. */
export function mpAd(ch: string, v: number) {
  const x = myCamp(); if (!x) return; const [, c] = x;
  if (!c.fund || c.done) return;
  c.ads = setAlloc(c.ads || {}, ch, v, c.fund.total);
}
